/**
 * CardBackground - Elegant card background with gradient
 * Minimalist design with Tipsy brand colors
 */
import React from 'react';
import { View, StyleSheet, ViewStyle, Platform, DimensionValue } from 'react-native';

export type CardBackgroundVariant = 'default' | 'premium' | 'frozen';

export interface CardBackgroundProps {
  variant?: CardBackgroundVariant;
  width?: DimensionValue;
  height?: DimensionValue;
  style?: ViewStyle;
  children?: React.ReactNode;
}

// Tipsy brand colors - gold gradient theme
const COLORS = {
  black: '#000000',
  darkGray: '#111111',
  goldLight: '#ffe998', // Light gold
  goldDark: '#57370d', // Dark gold/brown
  frozenBlue: '#2E86DE',
  frozenDark: '#1B4F72',
};

const getColors = (variant: CardBackgroundVariant) => {
  switch (variant) {
    case 'frozen':
      return {
        base: COLORS.frozenDark,
        secondary: '#2471A3',
        accent: COLORS.frozenBlue,
      };
    case 'premium':
      return {
        base: '#0A0A0A',
        secondary: '#1A1A1A',
        accent: COLORS.goldLight,
      };
    default:
      return {
        base: COLORS.black,
        secondary: COLORS.darkGray,
        accent: COLORS.goldLight,
      };
  }
};

export const CardBackground: React.FC<CardBackgroundProps> = ({
  variant = 'default',
  width = '100%',
  height = 200,
  style,
  children,
}) => {
  const colors = getColors(variant);

  // Web-specific gradient styles
  const webGradientStyle: ViewStyle | undefined =
    Platform.OS === 'web'
      ? ({
          background: `linear-gradient(145deg, ${colors.base} 0%, ${colors.secondary} 50%, ${colors.base} 100%)`,
        } as ViewStyle)
      : undefined;

  return (
    <View style={[styles.container, { width, height }, style]}>
      {/* Base layer */}
      <View style={[styles.baseLayer, { backgroundColor: colors.base }, webGradientStyle]} />

      {/* Native gradient simulation */}
      {Platform.OS !== 'web' && (
        <View style={[styles.gradientOverlay, { backgroundColor: colors.secondary }]} />
      )}

      {/* Subtle glow - top right */}
      <View style={[styles.glowTopRight, { backgroundColor: colors.accent }]} />

      {/* Subtle glow - bottom left */}
      <View style={[styles.glowBottomLeft, { backgroundColor: colors.accent }]} />

      {/* Top accent line */}
      <View style={[styles.topLine, { backgroundColor: colors.accent }]} />

      {/* Content */}
      <View style={styles.content}>{children}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  baseLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 16,
  },
  gradientOverlay: {
    position: 'absolute',
    top: '15%',
    left: '5%',
    right: '5%',
    bottom: '15%',
    opacity: 0.4,
    borderRadius: 80,
  },
  glowTopRight: {
    position: 'absolute',
    top: -60,
    right: -40,
    width: 140,
    height: 140,
    borderRadius: 70,
    opacity: 0.12,
  },
  glowBottomLeft: {
    position: 'absolute',
    bottom: -50,
    left: -30,
    width: 120,
    height: 120,
    borderRadius: 60,
    opacity: 0.08,
  },
  topLine: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    opacity: 0.7,
  },
  content: {
    ...StyleSheet.absoluteFillObject,
  },
});

export default CardBackground;
